import { Component } from '@theme/component';

/**
 * A custom element that manages the announcement bar sticky behavior.
 * Similar to the header sticky functionality but for the announcement bar.
 *
 * @extends {Component}
 */
class AnnouncementBarStickyComponent extends Component {
  /**
   * An intersection observer for monitoring sticky position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the announcement bar has been scrolled offscreen
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * The duration to wait for hiding animation
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Observes the announcement bar while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the bar is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: [0, 0.1, 1],
      rootMargin: '0px',
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting, intersectionRatio } = entry;

      if (alwaysSticky) {
        // For "always" mode
        this.dataset.stickyState = intersectionRatio >= 0.99 ? 'inactive' : 'active';
      } else {
        // For "scroll-up" mode
        if (!isIntersecting) {
          this.#offscreen = true;
        } else if (intersectionRatio >= 0.99) {
          this.#offscreen = false;
        }
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const barTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = scrollTop <= 0;
    const scrollDelta = Math.abs(scrollTop - this.#lastScrollTop);

    // Ignore very small scroll changes
    if (scrollDelta < 1) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // Scroll-up mode logic
    if (stickyMode === 'scroll-up') {
      if (isAtTop) {
        // At the very top of the page - show in natural position
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        this.#offscreen = false;
      } else if (isScrollingUp) {
        // Scrolling up - show sticky bar
        this.removeAttribute('data-animating');
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
        this.#offscreen = false;
      } else {
        // Scrolling down - hide sticky bar
        this.dataset.scrollDirection = 'down';
        
        if (this.dataset.stickyState === 'active' || this.dataset.stickyState === 'inactive') {
          this.setAttribute('data-animating', '');
          
          this.#timeout = setTimeout(() => {
            this.dataset.stickyState = 'idle';
            this.removeAttribute('data-animating');
            this.#offscreen = true;
          }, this.#animationDelay);
        } else {
          this.dataset.stickyState = 'idle';
          this.#offscreen = true;
        }
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode && stickyMode !== 'never') {
      // 设置初始状态
      if (stickyMode === 'scroll-up') {
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      }
      
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
  }
}

if (!customElements.get('announcement-bar-sticky-component')) {
  customElements.define('announcement-bar-sticky-component', AnnouncementBarStickyComponent);
}

